import { useGLTF } from "@react-three/drei";
import { useCallback, useMemo } from "react";
import { Box3, Vector3 } from "three";

export function useCenteredGltf(modelPath: string) {
  const gltf = useGLTF(modelPath);

  const { processedScene, center, modelBottomY, modelHeight } = useMemo(() => {
    const scene = gltf.scene.clone(true);
    scene.updateMatrixWorld(true);

    const box = new Box3().setFromObject(scene);
    // Center the model using the full bounding-box center (same approach as
    // decorations). The GLB files bake Blender world positions into node
    // transforms, and the manifest stores the same positions. Centering by
    // boxCenter removes the baked-in position so the group's manifest
    // position can re-apply it without double-counting.
    // Note: chairs shift boxCenter.y slightly upward compared to the table-
    // only center, which may cause table legs to clip marginally into the
    // floor — this is acceptable and matches the Blender scene.
    const centerPoint = box.getCenter(new Vector3());

    return {
      center: centerPoint,
      modelBottomY: box.min.y - centerPoint.y,
      modelHeight: box.max.y - box.min.y,
      processedScene: scene,
    };
  }, [gltf.scene]);

  const clonedGltf = useCallback(
    () => processedScene.clone(true),
    [processedScene]
  );

  return { center, clonedGltf, modelBottomY, modelHeight };
}
